import type { FC } from 'react';
import { FormattedMessage } from 'react-intl';

import type { EntityMedia } from '@xing-com/crate-common-graphql-types';
import { UpsellBannerVertical } from '@xing-com/crate-entity-pages-common';
import { getTrackingUrl } from '@xing-com/crate-entity-pages-common/tracking';
import { usePageContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';

import { AboutUsSkeleton } from './about-us-skeleton';
import * as Styled from './about-us.styles';
import { GalleryPreview } from './gallery-preview/gallery-preview';

type AboutUsProps = {
  pageSlug: string;
  headline?: string;
  summary: string;
  hasSubpage: boolean;
  galleryItems: Partial<EntityMedia>[];
  upsellActive?: boolean;
  isEditing: boolean;
};

export const AboutUs: FC<AboutUsProps> & {
  Skeleton: typeof AboutUsSkeleton;
} = ({
  upsellActive,
  headline,
  summary,
  pageSlug,
  hasSubpage,
  galleryItems,
  isEditing,
}) => {
  const { pageContext } = usePageContext();

  return (
    <Styled.Wrapper data-testid={'ABOUT_US_MODULE'}>
      <Styled.AboutUsWrapper>
        {upsellActive ? (
          <Styled.UpsellLayout>
            <UpsellBannerVertical
              headerCopyKey="EP_UPSELL_PROFILE_MULTIMEDIA_HEADER"
              bodyCopyKey="EP_UPSELL_PROFILE_MULTIMEDIA_BODY"
              moduleName="aboutusmodule"
            />
          </Styled.UpsellLayout>
        ) : (
          <GalleryPreview pageSlug={pageSlug} galleryItems={galleryItems} />
        )}
        <Styled.SummaryAwardsWrapper>
          <div data-cy={'aboutUsText'}>
            {headline && (
              <Styled.FlexRow>
                <Styled.Headline
                  fontWeight="bold"
                  size={'xlarge'}
                  data-testid={'ABOUTUS_HEADLINE'}
                >
                  {headline}
                </Styled.Headline>
                {isEditing && pageContext.isCompanyPage ? (
                  <Styled.EditButton
                    size={'small'}
                    showIcon={true}
                    data-cy="EDIT_BUTTON_ON_ABOUT_US_DESC_TITLE"
                    data-testid="EDIT_BUTTON_ON_ABOUT_US_DESC_TITLE"
                    to={`${pageContext.basePath}/${pageSlug}/edit/aboutUsDescription?type=short`}
                  />
                ) : null}
              </Styled.FlexRow>
            )}
            <Styled.FlexRow>
              <Styled.DescriptionText
                noMargin
                size={'small'}
                data-testid={'ABOUTUS_DESC'}
              >
                {summary}
              </Styled.DescriptionText>
              {!headline && isEditing && pageContext.isCompanyPage ? (
                <Styled.EditButton
                  size={'small'}
                  showIcon={true}
                  data-testid="edit-aboutus-description"
                  data-cy="EDIT_BUTTON_ON_ABOUT_US_DESC_DETAIL"
                  to={`${pageContext.basePath}/${pageSlug}/edit/aboutUsDescription?type=short`}
                />
              ) : null}
            </Styled.FlexRow>
            {hasSubpage && (
              <Styled.ButtonWrapper>
                <Styled.Button
                  variant={'tertiary'}
                  size={'small'}
                  to={getTrackingUrl({
                    to: `${pageContext.basePath}/${pageSlug}/about_us`,
                    trackingKey: 'discoverMore',
                  })}
                >
                  <FormattedMessage
                    id={'EP_ABOUT_US_DISCOVER_CTA'}
                    defaultMessage={'EP_ABOUT_US_DISCOVER_CTA'}
                  />
                </Styled.Button>
              </Styled.ButtonWrapper>
            )}
          </div>
        </Styled.SummaryAwardsWrapper>
      </Styled.AboutUsWrapper>
    </Styled.Wrapper>
  );
};

AboutUs.Skeleton = AboutUsSkeleton;
